import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { PublishButton } from '../pages/PublishButton';
import { useViewBranch } from '../view/ViewBranchContext';

import { BranchSelector } from './BranchSelector';
import { ProductionWarningBanner } from './ProductionWarningBanner';
import { ResetButton } from './ResetButton';
import { SearchDialog } from './SearchDialog';
import { UserMenu } from './UserMenu';

const NAV: { to: string; label: string }[] = [
  { to: '/', label: 'ファイル' },
  { to: '/recent', label: '最近の更新' },
  { to: '/glossary', label: '用語集' },
];

export const AppLayout = ({ children }: { children: ReactNode }) => {
  const { state } = useAuth();
  const { isReadOnly } = useViewBranch();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const signedIn = state.status === 'signed-in';

  // ⌘K (Mac) / Ctrl+K で検索を開く。input/textarea にフォーカスがあっても拾えるよう
  // window で listen する。Group ページの ⌘S 保存ショートカットとは別 key なので衝突しない。
  useEffect(() => {
    if (!signedIn) return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'k') return;
      e.preventDefault();
      setSearchOpen((v) => !v);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [signedIn]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-base font-semibold">
              UZU Translator
            </Link>
            <nav className="flex gap-4 text-sm">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className={
                    location.pathname === n.to
                      ? 'text-gray-900'
                      : 'text-gray-500 hover:text-gray-900'
                  }
                >
                  {n.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {signedIn && (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-500 hover:border-gray-300 hover:bg-gray-100"
                title="検索 (⌘K / Ctrl+K)"
              >
                <span>🔍 検索</span>
                <kbd className="rounded border bg-white px-1 py-0.5 text-[10px] text-gray-500">
                  ⌘K
                </kbd>
              </button>
            )}
            {signedIn && <BranchSelector />}
            {/* 公開・破棄は自分のブランチに対する書き込み操作。他者ブランチ閲覧中は隠す。 */}
            {signedIn && !isReadOnly && <ResetButton />}
            {signedIn && !isReadOnly && <PublishButton />}
            <UserMenu />
          </div>
        </div>
      </header>
      <ProductionWarningBanner />
      <main className="mx-auto max-w-6xl px-6 py-6">{children}</main>
      {signedIn && searchOpen && <SearchDialog onClose={() => setSearchOpen(false)} />}
    </div>
  );
};
